/**
 * MD-30 Phase B: Connect cards cho 4 nen tang social (FB / IG / Threads / LinkedIn).
 * Spec: F-MK-SOCIAL-MULTIPLATFORM.md §3.7 + AC-10
 *
 * Hien thi trong /dashboard tab Social (extend MD-29). Reuse dashboard auth gate.
 *
 * Endpoint reuse:
 *   GET  /api/v1/admin/social/accounts             -> list connected accounts
 *   GET  /api/v1/admin/social/oauth/authorize/:p   -> redirect provider
 *   POST /api/v1/admin/social/oauth/refresh/:id    -> manual token refresh
 *   POST /api/v1/admin/social/oauth/disconnect/:id -> revoke + soft delete
 *
 * VN copy day du dau (per CLAUDE.md VN copy rule).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

type Platform = 'facebook' | 'instagram' | 'threads' | 'linkedin';
type AccountStatus = 'active' | 'expired' | 'revoked' | 'error';

interface Account {
  id: number;
  platform: Platform;
  external_id: string;
  display_name: string;
  scope: string;
  status: AccountStatus;
  connected_at: number;
  last_refreshed_at: number | null;
  last_used_at: number | null;
  token_expires_at: number | null;
  last_error: string | null;
}

interface AccountsResponse {
  accounts: Account[];
}

const PLATFORM_LABEL: Record<Platform, { vi: string; en: string; help: string }> = {
  facebook: {
    vi: 'Facebook Page',
    en: 'Facebook Page',
    help: 'Tự động đăng bài lên Trang Facebook Asklaw.',
  },
  instagram: {
    vi: 'Instagram Business',
    en: 'Instagram Business',
    help: 'Đăng ảnh kèm chú thích lên tài khoản Instagram doanh nghiệp đã liên kết Facebook Page.',
  },
  threads: {
    vi: 'Threads',
    en: 'Threads',
    help: 'Đăng bài text ngắn (≤500 ký tự) lên Threads.',
  },
  linkedin: {
    vi: 'LinkedIn (Trang Tổ chức)',
    en: 'LinkedIn (Organization Page)',
    help: 'Đăng bài lên Trang Tổ chức LinkedIn — bản tiếng Việt rút gọn, không phải tư vấn pháp lý.',
  },
};

const STATUS_LABEL: Record<AccountStatus, string> = {
  active: 'Đã kết nối',
  expired: 'Token hết hạn — cần kết nối lại',
  revoked: 'Đã ngắt kết nối',
  error: 'Có lỗi — kiểm tra chi tiết',
};

function fmtDate(unix: number | null): string {
  if (!unix) return 'chưa có';
  const d = new Date(unix * 1000);
  return d.toLocaleString('vi-VN', { hour12: false });
}

function expiresInLabel(unix: number | null): string {
  if (!unix) return 'không hết hạn';
  const days = Math.floor((unix - Date.now() / 1000) / 86400);
  if (days < 0) return `đã hết hạn ${Math.abs(days)} ngày trước`;
  if (days === 0) return 'hết hạn hôm nay';
  if (days < 7) return `còn ${days} ngày — sắp hết hạn`;
  return `còn ${days} ngày`;
}

function getAuthHeader(): Record<string, string> {
  try {
    const token = localStorage.getItem('asklaw_access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function SocialConnectCards() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ id: number; label: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/social/accounts', { headers: getAuthHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = (await res.json()) as AccountsResponse;
      setAccounts(j.accounts ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const byPlatform = useMemo(() => {
    const map: Record<Platform, Account[]> = {
      facebook: [],
      instagram: [],
      threads: [],
      linkedin: [],
    };
    for (const a of accounts) map[a.platform].push(a);
    return map;
  }, [accounts]);

  const handleConnect = async (platform: Platform) => {
    // Backend authorize endpoint requires Bearer JWT (admin tier). Browser top-level
    // navigation cannot carry Authorization header, so we fetch the provider URL as
    // JSON then perform the redirect client-side.
    //
    // MD-30 workaround (s2026-05-15-MD30-OAUTH-START-POST-WORKAROUND): try the new
    // POST /oauth/start first because the URL no longer contains the platform
    // keyword (Chrome + extensions appear to pattern-match GET URL with
    // "facebook" + "oauth" + "authorize" and silently block — founder repro).
    // Fallback to legacy GET /oauth/authorize/:platform on 404 so older
    // deployments still work during rollout.
    setError(null);
    const authHeader = getAuthHeader();
    const hasToken = 'Authorization' in authHeader;
    if (!hasToken) {
      setError('Không tìm thấy token đăng nhập (asklaw_access_token). Vui lòng đăng nhập lại.');
      console.error('[social-connect] missing asklaw_access_token in localStorage');
      return;
    }
    const returnTo = '/dashboard/content?tab=social';
    const postUrl = '/api/v1/admin/social/oauth/start';
    console.info(`[social-connect] handleConnect platform=${platform} url=${postUrl} (POST)`);
    let res: Response | null = null;
    try {
      res = await fetch(postUrl, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, return_to: returnTo }),
      });
    } catch (e) {
      const msg = (e as Error).message;
      console.warn(`[social-connect] POST /oauth/start network fail: ${msg} — will try GET fallback`);
      res = null;
    }
    // Fallback to GET legacy endpoint if POST is 404 (older worker) or POST
    // raised a network error (so we still have a second chance). Note: the GET
    // path contains "facebook"/"oauth"/"authorize" so it may also be blocked,
    // but at least it preserves backward compat during deploy lag.
    if (!res || res.status === 404) {
      const getUrl = `/api/v1/admin/social/oauth/authorize/${platform}?return_to=${encodeURIComponent(returnTo)}`;
      console.info(`[social-connect] fallback GET url=${getUrl}`);
      try {
        res = await fetch(getUrl, { headers: authHeader });
      } catch (e) {
        const msg = (e as Error).message;
        console.error(`[social-connect] fetch network fail (both POST + GET): ${msg}`);
        setError(
          `Không gửi được yêu cầu (lỗi mạng/trình duyệt): ${msg}. Vui lòng tắt extension chặn quảng cáo (uBlock, Privacy Badger, Brave Shields) hoặc thử Incognito.`,
        );
        return;
      }
    }
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      const msg = j.error ?? `HTTP ${res.status}`;
      console.error(`[social-connect] backend ${res.status}: ${msg}`);
      setError(`Backend từ chối yêu cầu (HTTP ${res.status}): ${msg}`);
      return;
    }
    let j: { authorize_url?: string; redirect_uri_sent?: string };
    try {
      j = (await res.json()) as { authorize_url?: string; redirect_uri_sent?: string };
    } catch (e) {
      console.error('[social-connect] JSON parse fail', (e as Error).message);
      setError(`Phản hồi backend không phải JSON hợp lệ: ${(e as Error).message}`);
      return;
    }
    if (!j.authorize_url) {
      setError('Thiếu authorize_url trong phản hồi backend.');
      return;
    }
    // Diagnostic: log redirect URI being sent to provider. Mismatch with FB/LinkedIn
    // dev console Valid OAuth Redirect URIs = provider rejects after user clicks login.
    console.info(`[social-connect] redirect_uri_sent=${j.redirect_uri_sent ?? '(not echoed)'}`);
    console.info(`[social-connect] authorize_url=${j.authorize_url}`);
    window.location.href = j.authorize_url;
  };

  const handleRefresh = async (id: number) => {
    setBusyId(id);
    try {
      const res = await fetch(`/api/v1/admin/social/oauth/refresh/${id}`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `HTTP ${res.status}`);
      }
      await load();
    } catch (e) {
      alert(`Làm mới token thất bại: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleDisconnect = async (id: number) => {
    setBusyId(id);
    try {
      const res = await fetch(`/api/v1/admin/social/oauth/disconnect/${id}`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert(`Ngắt kết nối thất bại: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  return (
    <section className="social-connect-cards" aria-label="Kết nối tài khoản mạng xã hội">
      <header className="social-connect-header">
        <h2 className="social-connect-title">Kết nối tài khoản mạng xã hội</h2>
        <p className="social-connect-sub">
          Mỗi nền tảng dùng OAuth để xin quyền đăng bài. Token được mã hoá AES-256 trước khi lưu, tự động làm mới hằng
          ngày. Có thể ngắt kết nối bất kỳ lúc nào — Asklaw sẽ thu hồi quyền tại nền tảng tương ứng và xoá token cục bộ.
        </p>
        <button type="button" className="social-connect-reload" onClick={() => void load()}>
          Tải lại trạng thái
        </button>
      </header>

      {loading && <p className="social-connect-loading">Đang tải trạng thái kết nối...</p>}
      {error && <p className="social-connect-error">Lỗi tải trạng thái: {error}</p>}

      <div className="social-connect-grid">
        {(Object.keys(PLATFORM_LABEL) as Platform[]).map((platform) => {
          const list = byPlatform[platform];
          const active = list.find((a) => a.status === 'active');
          const labels = PLATFORM_LABEL[platform];
          return (
            <article key={platform} className={`social-connect-card social-connect-card-${platform}`}>
              <header className="social-connect-card-head">
                <h3 className="social-connect-card-title">{labels.vi}</h3>
                <StatusBadge status={active ? 'active' : (list[0]?.status ?? 'revoked')} hasAny={list.length > 0} />
              </header>
              <p className="social-connect-card-help">{labels.help}</p>

              {active ? (
                <div className="social-connect-card-active">
                  <div className="social-connect-line">
                    <span className="social-connect-line-label">Tài khoản:</span>
                    <span className="social-connect-line-value">{active.display_name}</span>
                  </div>
                  <div className="social-connect-line">
                    <span className="social-connect-line-label">Hết hạn token:</span>
                    <span className="social-connect-line-value">{expiresInLabel(active.token_expires_at)}</span>
                  </div>
                  <div className="social-connect-line">
                    <span className="social-connect-line-label">Lần làm mới gần nhất:</span>
                    <span className="social-connect-line-value">{fmtDate(active.last_refreshed_at)}</span>
                  </div>
                  <div className="social-connect-actions">
                    <button
                      type="button"
                      className="social-connect-btn social-connect-btn-refresh"
                      onClick={() => void handleRefresh(active.id)}
                      disabled={busyId === active.id}
                    >
                      {busyId === active.id ? 'Đang làm mới...' : 'Làm mới token ngay'}
                    </button>
                    <button
                      type="button"
                      className="social-connect-btn social-connect-btn-disconnect"
                      onClick={() => setConfirm({ id: active.id, label: active.display_name })}
                      disabled={busyId === active.id}
                    >
                      Ngắt kết nối
                    </button>
                  </div>
                  {active.last_error && (
                    <p className="social-connect-card-error">Lỗi gần nhất: {active.last_error}</p>
                  )}
                </div>
              ) : (
                <div className="social-connect-card-empty">
                  {list[0]?.status === 'expired' && (
                    <p className="social-connect-card-warn">Token đã hết hạn — bấm để cấp quyền lại.</p>
                  )}
                  {list[0]?.status === 'error' && (
                    <p className="social-connect-card-warn">
                      Có lỗi: {list[0].last_error ?? 'không rõ'}. Bấm để cấp quyền lại.
                    </p>
                  )}
                  <button
                    type="button"
                    className="social-connect-btn social-connect-btn-connect"
                    onClick={() => handleConnect(platform)}
                  >
                    Kết nối tài khoản
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {confirm && (
        <div className="social-connect-modal" role="dialog" aria-modal="true">
          <div className="social-connect-modal-body">
            <h3>Xác nhận ngắt kết nối</h3>
            <p>
              Asklaw sẽ thu hồi quyền tại nền tảng và xoá token cục bộ cho tài khoản{' '}
              <strong>{confirm.label}</strong>. Hành động này không xoá các bài đã đăng.
            </p>
            <div className="social-connect-modal-actions">
              <button
                type="button"
                className="social-connect-btn social-connect-btn-cancel"
                onClick={() => setConfirm(null)}
              >
                Huỷ
              </button>
              <button
                type="button"
                className="social-connect-btn social-connect-btn-disconnect"
                onClick={() => void handleDisconnect(confirm.id)}
              >
                Ngắt kết nối
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function StatusBadge({ status, hasAny }: { status: AccountStatus; hasAny: boolean }) {
  const cls =
    status === 'active'
      ? 'social-connect-badge-ok'
      : status === 'expired'
      ? 'social-connect-badge-warn'
      : status === 'error'
      ? 'social-connect-badge-err'
      : 'social-connect-badge-off';
  const label = hasAny ? STATUS_LABEL[status] : 'Chưa kết nối';
  return <span className={`social-connect-badge ${cls}`}>{label}</span>;
}
